import { ComponentType, useId } from "react";
import Head from "next/head";
import Script from "next/script";
import Router from "next/router";
import NProgress from "nprogress";
import { ApolloProvider } from "@apollo/client";
import dynamic from "next/dynamic";
import { useApollo } from "../apollo";
import "../styles/global.scss";
import "../../src/components/UI/LeftSliderSection/slider.scss";
import NotFoundPage from "components/NotFoundPage/NotFoundPage";
import "swiper/scss";
import "swiper/scss/pagination";
import "react-toastify/dist/ReactToastify.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "primeicons/primeicons.css";
import "../../public/css/font/icomoon/style.css";

const Asset = dynamic(() => import("common/Asset"), {
	ssr: false,
});

const Layout = dynamic(() => import("Layout"), {
	ssr: false,
});

const MetaTagsList = dynamic(() => import("common/MetaTagsList"), {
	ssr: true,
});

NProgress.configure({
	showSpinner: false,
});

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

function App({ Component, pageProps }: { Component: ComponentType; pageProps: any }) {
	if (pageProps?.notLocaleFound) {
		return <NotFoundPage />;
	}
	const { data, language, tld, initialApolloState } = pageProps;
	const client = useApollo(language || "en", tld || "com", initialApolloState || {});
	const randomId = useId();
	const {
		metatagInformation = null,
		title = "",
		nid = null,
		domain = "",
		googleAnalytics = null,
		hrefLang = null,
		breadcrumb = [],
	} = data || {};

	return (
		<>
			{metatagInformation && metatagInformation?.length > 0 && (
				<MetaTagsList metaTags={metatagInformation || []} title={title} nid={nid} domain={domain} />
			)}
			{googleAnalytics && googleAnalytics.length > 0 && (
				<Head>
					{googleAnalytics[0].analyticsURL && googleAnalytics[0].analyticsURL.length > 0 && (
						<script src={googleAnalytics[0]?.analyticsURL} />
					)}
					{googleAnalytics[0].snippet1 && googleAnalytics[0].snippet1.length > 0 && (
						<script src={googleAnalytics[0]?.snippet1} />
					)}
					{googleAnalytics[0].snippet2 && googleAnalytics[0].snippet2.length > 0 && (
						<script src={googleAnalytics[0]?.snippet2} />
					)}
					{googleAnalytics[0].snippet3 && googleAnalytics[0].snippet3.length > 0 && (
						<Script>{googleAnalytics[0]?.snippet3}</Script>
					)}
					{googleAnalytics[0].gmtSnippet && googleAnalytics[0].gmtSnippet.length > 0 && (
						<Script>{googleAnalytics[0]?.gmtSnippet}</Script>
					)}
				</Head>
			)}
			{hrefLang && hrefLang.length > 0 && (
				<Head>
					{hrefLang &&
						hrefLang.map((item: { href: string; hrefLang: string; rel: string }, index: number) => {
							return <link key={`${randomId}-${index}`} rel={item?.rel} href={item?.href} hrefLang={item?.hrefLang} />;
						})}
				</Head>
			)}
			{breadcrumb && breadcrumb[0] && breadcrumb[0]?.schema ? (
				<Head>
					<script type="application/ld+json">{JSON.stringify(JSON.parse(breadcrumb[0]?.schema))}</script>
				</Head>
			) : null}
			<ApolloProvider client={client}>
				<Layout pageProps={pageProps}>
					<Asset />
					<Component {...pageProps} />
				</Layout>
			</ApolloProvider>
		</>
	);
}

export default App;
